import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  signal,
  WritableSignal,
  effect,
  untracked,
  inject,
  Injector,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { Router } from '@angular/router';
import { LazyLoadEvent } from 'primeng/api';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  ActiveFilterService,
  ColumnDefinitionService,
  MetricsGridConfig,
  BreadcrumbService,
  IGridColumnGroup,
  GridConfigService,
  ClientProfileService,
  GridTableNgComponent,
  FormatService,
  GridSortingService,
  LogService,
} from '@/app/core';
import { SalesRepService } from './sales-rep.service';
import { DownloadsService } from '@/app/downloads';
import { HeaderService } from '@/app/header';
import { PageOptionsNg, SortOptionsNg, SortOptions } from '@/app/models';
import { AuthenticationService } from '@/app/core/authentication/authentication.service';
import { SalesRepCustomerService } from './sales-rep-customer.service';
import { QueryParams } from '@/app/core/http/query-params';
import { FilterProfileUtilsService } from '@/app/filter/profiles/filter-profile-utils.service';
import { SavedViewsService } from '@/app/modules/saved-views/saved-views.service';
import { createGridSerializer } from '@/app/modules/saved-views/saved-views-serializer';
import { savedViewDeserializer } from '@/app/modules/saved-views/saved-views-deserializer';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';
import {
  SavedView,
  SavedViewDeserializerResponse,
} from '@/app/modules/saved-views/types';

@Component({
  template: `
    <rdo-breadcrumb />
    <rdo-overlay [enabled]="isLoading()" />
    <div class="container-fluid" id="sales-rep">
      <div class="row">
        <div class="col-12" id="rdo-grid">
          <ng-container *featureFlag="'flagSavedViews'">
            <rdo-saved-views
              [gridName]="gridName"
              [pageName]="
                translateService.instant(
                  'main.core.common.counts.sales_reps.all'
                )
              "
            />
          </ng-container>
          @if (salesReps) {
            <rdo-grid-table-ng
              #grid
              [name]="gridName"
              [gridConfig]="gridConfig()"
              [pagedData]="salesReps"
              [sorting]="sorting"
              [(paging)]="paging"
              (pageOnChange)="changePage($event)"
              [scrollScale]="'largeScale'"
              (lazyLoadOnChange)="changeLazyLoad($event)"
              [totalCount]="totalCount"
              [frozenWidth]="frozenWidth"
              [showColumnSelector]="true"
              (visibilityChanged)="visibilityChanged($event)"
              (downloadExcelClick)="exportExcel($event)"
            />
          }
        </div>
      </div>
    </div>
  `,
  styleUrls: ['sales-rep.component.scss'],
  standalone: false,
})
export class SalesRepsComponent implements OnInit, OnDestroy {
  isLoading = signal(true);
  salesReps: any;
  gridConfig: WritableSignal<MetricsGridConfig> = signal(null);
  public areFiltersSubscribed = signal(false);
  frozenRows: any;
  paging = new PageOptionsNg();
  sorting: SortOptionsNg;
  totalCount = 0;
  frozenWidth: string;
  gridName = 'SALESREPS_GRID';
  calculatedProductTypeLength = 175;
  private subscriptions: Array<Subscription> = [];
  salesRepsRequestInProgress: Subscription;
  private logger;

  @ViewChild('grid') grid: GridTableNgComponent;
  constructor(
    private authenticationService: AuthenticationService,
    private filterService: ActiveFilterService,
    private salesRepsService: SalesRepService,
    private columnService: ColumnDefinitionService,
    private breadcrumbService: BreadcrumbService,
    private headerService: HeaderService,
    private downloadsService: DownloadsService,
    private formatService: FormatService,
    private customerService: SalesRepCustomerService,
    private gridConfigService: GridConfigService,
    private clientProfileService: ClientProfileService,
    private gridSortingService: GridSortingService,
    private router: Router,
    private filterProfileUtilsService: FilterProfileUtilsService,
    public translateService: TranslateService,
    public savedViewsService: SavedViewsService,
    protected featureFlagService: FeatureFlagService,
    protected logService: LogService
  ) {
    this.logger = logService.getLogger('SalesRepsComponent', false);

    effect(async () => {
      const areSavedViewsEnabled =
        this.featureFlagService.isActive('flagSavedViews');
      if (!areSavedViewsEnabled) {
        return;
      }
      const selectedView = this.savedViewsService.getSelectedView();
      const viewName = selectedView?.name ?? null;

      // We don't want the effect to re-run when lastAppliedViewName changes
      const lastApplied = untracked(
        () => this.savedViewsService.previouslySelectedViewsStack()[0] ?? null
      );
      if (viewName === lastApplied) {
        return;
      }

      // Use untracked to read gridConfig
      const hasGridConfig = untracked(() => !!this.gridConfig());

      if (selectedView && hasGridConfig) {
        // Call handler in untracked context to avoid tracking any signal reads inside
        untracked(async () => {
          const currentConfig = this.gridConfig();

          // Apply the saved view changes to the grid config
          const response = await this.savedViewsService.handleSavedViewChange(
            selectedView,
            currentConfig,
            this.getDeserializer()
          );

          this.logger.info('saved view change', response.config);

          if (!response || !response.config) {
            this.logger.error(
              '[EFFECT] No config returned from handleSavedViewChange, aborting'
            );
            return;
          }

          // Update the signal - this will automatically trigger change detection
          this.gridConfig.set(response.config);

          // Update the gridSortingService - this is the single source of truth
          // The sorting getter will automatically read from this service
          this.gridSortingService.setSortOption(
            response.sorting.key,
            response.sorting.direction === 'desc' // setSortOption expects descending as boolean
          );

          // Update local sorting for template binding (avoids getter performance issues)
          this.sorting = this.gridSortingService.getSortOptionsNg();

          this.paging.page = 1;
          if (this.grid) {
            this.grid.resetPaginator();
          }

          this.load();

          // Signal that the full saved view application is complete
          this.savedViewsService.notifyViewApplied();
        });
      }
    });
  }

  ngOnInit() {
    const areSavedViewsEnabled =
      this.featureFlagService.isActive('flagSavedViews');

    if (areSavedViewsEnabled) {
      this.savedViewsService.initGrid(this.gridName, this.getSerializer());
    }

    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    this.gridSortingService.setGridName(this.gridName);
    this.sorting = this.gridSortingService.getSortOptionsNg();

    this.isLoading.set(true);
    this.breadcrumbService.update([
      { title: 'main.core.common.counts.sales_reps.all', class: 'active' },
    ]);

    this.logger.info('ngOnInit');

    this.clientProfileService.getClientAttributes().subscribe((attributes) => {
      this.subscriptions.push(
        this.authenticationService.userInfoView.subscribe((userInfo) => {
          if (this.gridConfig()) {
            const config = this.gridConfig();
            config.enableExcelExport = userInfo.HasClientAccessToExportData;
            this.gridConfig.set(config);
            this.logger.info(
              `authenticationService.userInfoView User info updated, Excel export enabled: ${userInfo.HasClientAccessToExportData}`
            );
          }
        })
      );

      this.calculatedProductTypeLength = attributes.MaxProdTypeLen * 7.25;
      this.calculatedProductTypeLength =
        this.calculatedProductTypeLength < 175
          ? 175
          : this.calculatedProductTypeLength;
      this.configureGrid(true);
      this.beginLoad();
    });
  }

  ngOnDestroy() {
    _.each(this.subscriptions, (s) => s.unsubscribe());
  }

  beginLoad = async () => {
    this.logger.info('beginLoad');
    this.isLoading.set(true);
    this.logger.info('sales-reps.component.beginLoad');
    const areSavedViewsEnabled =
      this.featureFlagService.isActive('flagSavedViews');
    if (areSavedViewsEnabled) {
      await this.savedViewsService.load();
      // setDefaultView will resolve only after:
      // 1. No filter profile modal is needed (filters already match), OR
      // 2. The filter profile modal is accepted or declined by the user
      try {
        await this.savedViewsService.setDefaultView();
      } catch (e) {
        // User declined or view not found — continue with default grid state
        this.logger.warn('Default view not applied:', e);
      }
    }
    this.load(true);
  };

  load = (initEvent?: boolean) => {
    this.logger.info('sales-reps.component.load  ');
    if (this.salesRepsRequestInProgress) {
      this.salesRepsRequestInProgress.unsubscribe();
    }

    this.isLoading.set(true);

    const params = new QueryParams();

    if (
      this.gridConfig().groups[1].visible ||
      (initEvent && this.isStoredValueVisible(1))
    ) {
      params.set('groupByCustomer', 'true');
    }
    if (
      this.gridConfig().groups[2].visible ||
      (initEvent && this.isStoredValueVisible(2))
    ) {
      params.set('groupByProductType', 'true');
    }
    if (this.filterService.getCurrentFilter().useRouseSchema) {
      params.set('useRouseSchema', 'true');
    }

    this.salesRepsRequestInProgress = this.salesRepsService
      .getSalesReps(this.paging, this.getSorting(), params)
      .subscribe({
        next: (x) => {
          this.isLoading.set(false);

          this.totalCount = x.TotalCount;
          if (x.Items && x.Items.length > 0) {
            this.salesReps = x.Items;
          } else {
            this.salesReps = [];
          }

          if (!this.areFiltersSubscribed()) {
            this.areFiltersSubscribed.set(true);
            this.subscriptions.push(
              this.filterService.filterChange.subscribe(() => {
                // If saved views is currently applying a view or its filters, skip —
                // the saved view effect will handle configureGrid + load.
                if (this.savedViewsService.isApplying()) {
                  this.logger.info(
                    'filterChange SKIPPED — saved view is applying'
                  );
                  return;
                }
                this.paging.page = 1;
                this.grid?.resetPaginator();
                this.configureGrid(true);
                this.logger.info(
                  'filterChange subscription triggered, grid reconfigured'
                );
                this.load(true);
              })
            );
          }

          this.authenticationService.finishSentryTransaction();
        },
        error: (err) => {
          this.isLoading.set(false);
          this.logger.error('Failed to load sales reps:', err);
        },
      });
  };

  isStoredValueVisible(index: number): boolean {
    return (
      this.gridConfigService.getConfig(this.gridName) &&
      this.gridConfigService.getConfig(this.gridName)[index] &&
      this.gridConfigService.getConfig(this.gridName)[index].visible
    );
  }

  changePage = (event: any) => {
    if (event.rows !== this.paging.pageSize) {
      this.paging.pageSize = event.rows;
    }

    this.paging.page = event.first < 1 ? 1 : event.first / event.rows + 1;
    this.load();
  };

  changeLazyLoad = (event: LazyLoadEvent) => {
    if (
      this.sorting.sortField === event.sortField &&
      this.sorting.sortOrder === event.sortOrder
    ) {
      return;
    }

    this.gridSortingService.setSortOption(
      event.sortField,
      event.sortOrder === -1
    );

    this.sorting.sortField =
      event.sortField || this.gridSortingService.getSortFieldOrDefault();
    this.sorting.sortOrder =
      event.sortOrder || this.gridSortingService.getSortDirectionOrDefault();

    this.load();
  };

  visibilityChanged = (event: IGridColumnGroup) => {
    const countHeader = 'main.core.common.counts.customers.count';
    const productTypeHeader = this.filterProfileUtilsService
      .getProductTypesHeader()
      .ambiguousCount();
    const reloadColumns = [productTypeHeader, countHeader];
    const config = this.gridConfig();
    if (event.columnSelectorTitle === countHeader) {
      config.groups[3].visible = !event.visible;
    }
    config.groups[0].columns[0].isGroupingEnabled =
      config.groups[1].visible || config.groups[2].visible;

    this.gridConfig.set(config);

    if (reloadColumns.includes(event.columnSelectorTitle)) {
      this.paging.page = 1;
      this.grid.resetPaginator();
      this.load();
    }
  };

  exportExcel = (args: any) => {
    this.isLoading.set(true);

    const translatedConfig = this.gridConfig().cloneAndTranslate((text) =>
      this.formatService.translateAndFormat(text, false)
    );
    this.subscriptions.push(
      this.salesRepsService
        .getSalesRepsDownload(this.getSorting(), translatedConfig)
        .subscribe((blob) => {
          this.isLoading.set(false);
          this.downloadsService.saveExcelBlob(blob);
        })
    );
  };

  private configureGrid = (initEvent?: boolean) => {
    // Capture current visibility settings before recreating config, keyed by id or columnSelectorTitle
    const existingConfig = this.gridConfig();
    const existingVisibilityMap = new Map<string, boolean>();
    if (existingConfig?.groups) {
      existingConfig.groups.forEach((g) => {
        const key = g.id || g.columnSelectorTitle;
        if (key) {
          existingVisibilityMap.set(key, g.visible);
        }
      });
    }

    const salesRepNameWidth = 225;
    const customerCountColumn = this.columnService.CustomerCount();
    if (
      initEvent &&
      this.gridSortingService.getSortFieldOrDefault() === 'CustomerCount'
    ) {
      customerCountColumn.width += 15;
    }
    const transactionCountColumn = this.columnService.TransactionCount();
    if (
      initEvent &&
      this.gridSortingService.getSortFieldOrDefault() === 'TransactionCount'
    ) {
      transactionCountColumn.width += 15;
    }
    this.frozenWidth = `${salesRepNameWidth + (customerCountColumn.width || 110) + (transactionCountColumn.width || 110)}px`;

    const config = new MetricsGridConfig(
      [
        {
          title: '',
          visible: true,
          locked: true,
          columnSelectorTitle: 'main.core.common.counts.sales_reps.singular',
          columns: [
            {
              title: 'main.core.common.counts.sales_reps.singular',
              field: 'SalesRepName',
              sortColumn: 'SalesRepName',
              width: 100,
              autoWidth: true,
              maxWidth: 255,
              headerStyle: {
                'text-align': 'left',
              },
              cellStyle: {
                'text-align': 'left',
              },
              linkDsl: (v, r) => {
                const sorting = this.getSorting();
                const params = {
                  sortOn: sorting.sortOn,
                  desc: sorting.descending,
                };
                return ['.', r.SalesRepId, params];
              },
            },
          ],
        },
        {
          id: 'customer-name',
          title: '',
          visible: false,
          locked: true,
          forceCanClose: true,
          keyColumn: true,
          columnSelectorTitle: 'main.core.common.counts.customers.count',
          columns: [
            {
              title: 'main.core.common.counts.customers.count',
              field: 'CustomerName',
              sortColumn: 'CustomerName',
              headerStyle: {
                'text-align': 'left',
              },
              cellStyle: {
                'text-align': 'left',
              },
              width: 100,
              maxWidth: 180,
              autoWidth: true,
              isString: true,
            },
          ],
        },
        {
          title: '',
          visible: false,
          locked: true,
          forceCanClose: true,
          keyColumn: true,
          columnSelectorTitle: this.filterProfileUtilsService
            .getProductTypesHeader()
            .ambiguousCount(), //'main.core.common.counts.product_types.count',
          columns: [
            {
              title: this.filterProfileUtilsService
                .getProductTypesHeader()
                .ambiguousCount(), //'main.core.common.counts.product_types.count',
              field: 'ProductTypeDescription',
              sortColumn: 'ProductTypeDescription',
              headerStyle: {
                'text-align': 'left',
              },
              cellStyle: {
                'text-align': 'left',
              },
              width: 100,
              maxWidth: 500,
              minWidth: 115,
              autoWidth: true,
            },
          ],
        },
        {
          id: 'customer-count',
          title: '',
          visible: true,
          locked: true,
          columnSelectorTitle: 'main.core.common.counts.customers.count',
          columns: [customerCountColumn],
        },
        {
          title: '',
          visible: true,
          locked: true,
          columnSelectorTitle: 'main.core.common.counts.transactions.count',
          columns: [transactionCountColumn],
        },
        ...this.columnService.StandardMetricSetColumnGroups(true),
      ],
      (p) => this.loadChildren(p),
      true
    );
    // Read localStorage and populate configsCache so isStoredValueVisible() works on initial load
    this.gridConfigService.applyVisibilityOptions(this.gridName, config);

    // Restore visibility settings from existing config (if any) to preserve saved view state
    if (existingVisibilityMap.size > 0) {
      config.groups.forEach((group) => {
        const key = group.id || group.columnSelectorTitle;
        if (key && existingVisibilityMap.has(key)) {
          group.visible = existingVisibilityMap.get(key);
        }
      });
    }

    this.gridConfig.set(config);
  };

  private loadChildren = (parent: any): Observable<Array<any>> => {
    const sorting = this.getSorting();

    return this.customerService
      .getSalesRepCustomers(parent.SalesRepId, this.paging, sorting)
      .pipe(
        map((result) => {
          return result.Items.map(function (item) {
            const clone = (<any>Object).assign({}, item);
            clone.SalesRepId = parent.SalesRepId;
            clone.SalesRepName = null;
            clone.CustomerCount = item.CustomerName;
            return clone;
          });
        })
      );
    // return this.customerService.getSalesRepCustomersProductTypes(parent.SalesRepId, parent.CustomerId, sorting)
    // 	.pipe(map(items => {
    // 		return items.map(this.convert)
    // 	}));
  };

  private convert = (salesRepId: any, item: any): any => {
    const clone = (<any>Object).assign({}, item);
    clone.CustomerName = item.CustomerName;
    clone.ProductTypeCount = item.ProductTypeCount;
    return clone;
  };

  private getSorting = (): SortOptions => {
    this.sorting.sortField = this.gridSortingService.getSortFieldOrDefault();
    this.sorting.sortOrder =
      this.gridSortingService.getSortDirectionOrDefault();
    return new SortOptions(
      this.sorting.sortField,
      !(this.sorting.sortOrder === 1)
    );
  };

  /*
  private getSerializer(): () => SavedViewSerialized {
    return (): SavedViewSerialized => {
      const config = this.gridConfig();
      this.logger.info('gridSerializer  called with\n', config);
      const result: Partial<SavedViewSerialized> = {
        grid_id: this.gridName,
        columns: [],
      };

      config?.groups.forEach((group, index) => {
        if (group.visible) {
          // Using columnSelectorTitle as identifier because title can be empty.
          result.columns.push(
            group.columnSelectorTitle || `__group_${index}__`
          );
          // we don't have to deal with individual columns because columns are display
          // or hidden by group.
          // Even the 2 columns that appear in the left most "frozen" section are
          // controlled by the group visibility.
        }
      });

      const currentSortField = this.gridSortingService.getSortFieldOrDefault();
      const currentSortOrder =
        this.gridSortingService.getSortDirectionOrDefault();

      result.sort = [
        {
          key: currentSortField,
          direction: currentSortOrder === 1 ? 'asc' : 'desc',
          priority: 1,
        },
      ];

      this.logger.info('gridSerializer  returning\n', result);

      return result as SavedViewSerialized;
    };
  }
  */

  private getDeserializer = () => {
    return async (
      config: MetricsGridConfig,
      view: SavedView
    ): Promise<SavedViewDeserializerResponse<MetricsGridConfig>> => {
      this.logger.info(
        'gridDeserializer  called with view\n',
        view,
        '\ncurrent config\n',
        config
      );
      const newConfig = await savedViewDeserializer(config, view);
      const customerNameGroup = newConfig.config.groups.find(
        (group) => group.id === 'customer-name'
      );
      if (customerNameGroup?.visible) {
        const customerCountGroup = newConfig.config.groups.find(
          (group) => group.id === 'customer-count'
        );
        if (customerCountGroup) {
          customerCountGroup.visible = false;
        }
      }

      // I'm not sure this is necessary, but this is in the visibility toggle logic.
      newConfig.config.groups[0].columns[0].isGroupingEnabled =
        newConfig.config.groups[1].visible ||
        newConfig.config.groups[2].visible;

      return Promise.resolve(newConfig);
    };
  };

  private getSerializer = createGridSerializer(
    {
      gridName: this.gridName,
      gridConfig: this.gridConfig,
      gridSortingService: this.gridSortingService,
      componentName: 'sales-reps.component',
    },
    true
  );
}
